import * as React from 'react';
import { useContext, useMemo } from 'react';
import { DD_SLASH_MMM_SLASH_YYYY } from 'constants/dateFormat';
import { ROLE_FINANCE } from 'constants/userRoles';
import moment from 'moment';
import { userHasRole } from 'services/auth';
import { toBill } from 'services/financial';
import { muiDataGridLocaleText } from 'services/i18n/mui';

import { formatDate } from '../../../../../services/date';
import { eventNumberComparator } from '../../NewInvoice/InvoiceTable/InvoiceTableService';
import { Amount } from '../../shared/amount-view/Amount/Amount';
import { studyTypeOptionsMap } from '../../shared/FinanceFilters/MultiSelectStudyType';
import { finStudyTypeFilterOperator } from '../../shared/FinanseTableMUI/CustomFilterOperators';
import { DefaultFinanceCell } from '../../shared/FinanseTableMUI/DefaultFinanceCell';
import { FinanceTableMUI } from '../../shared/FinanseTableMUI/FinanceTableMUI';
import { prepareNameForCSV } from '../../shared/FinanseTableMUI/FinanceTableServices';
import { LedgerContext } from '../LedgerContext';

import { PatientLedgerMenu } from './LadgerPatientMenu/PatientLedgerMenu';

export const LedgerTable = () => {
  const { ledgerEvents } = useContext(LedgerContext);

  const columns = useMemo(
    () => [
      {
        field: 'eventNumber',
        headerName: 'Event ID',
        flex: 1,
        minWidth: 125,
        sortComparator: eventNumberComparator,
        valueGetter: (value, row) => {
          if (row.adjustmentSequence === 0) {
            return `${row.eventNumber}`;
          }
          return `${row.eventNumber}.${row.adjustmentSequence}`;
        }
      },
      {
        field: 'studyName',
        headerName: 'Study',
        flex: 1,
        minWidth: 110,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'finStudyType',
        headerName: 'Study Type',
        flex: 1,
        minWidth: 145,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value),
        valueGetter: value => studyTypeOptionsMap.find(option => option.id === value)?.name || value,
        filterOperators: finStudyTypeFilterOperator
      },
      {
        field: 'siteName',
        headerName: 'Site',
        flex: 1,
        minWidth: 95,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'patientId',
        headerName: 'Patient ID',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'subjectId',
        headerName: 'Subject ID',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => (value != null ? `[${value}]` : '')
      },
      {
        field: 'encounterName',
        headerName: 'Encounter',
        flex: 1,
        minWidth: 135,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'elligoEventType',
        headerName: 'Budget Event Type',
        flex: 1,
        minWidth: 190,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'itemGroupName',
        headerName: 'Item Group',
        flex: 1,
        minWidth: 140,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'eventName',
        headerName: 'Event Name',
        flex: 1,
        minWidth: 150,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      },
      {
        field: 'ledgerEventStatuses.eventStatus',
        headerName: 'Status',
        flex: 1,
        minWidth: 115,
        valueGetter: (value, row) => row.ledgerEventStatuses.eventStatus,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'eventDate',
        headerName: 'Date',
        flex: 1,
        minWidth: 115,
        type: 'date',
        valueFormatter: value => formatDate.forCsv(value),
        valueGetter: (value, row) => value && new Date(value),
        renderCell: ({ row }) => (
          <div className="overflow-ellipsis">{moment(row.eventDate).format(DD_SLASH_MMM_SLASH_YYYY)}</div>
        )
      },
      {
        field: 'amount',
        headerName: 'Amount',
        flex: 1,
        minWidth: 120,
        type: 'number',
        valueGetter: (value, row) => toBill(row.amountForDisplay),
        renderCell: ({ row }) => (
          <Amount className="overflow-ellipsis" coinsAmount={row.amountForDisplay} showDollarSign />
        )
      },
      {
        field: 'ledgerEventStatuses.eventItemStatuses.CLIENT',
        headerName: 'Client',
        flex: 1,
        minWidth: 110,
        valueGetter: (value, row) => row.ledgerEventStatuses.eventItemStatuses.CLIENT,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'ledgerEventStatuses.eventItemStatuses.SITE',
        headerName: 'Site',
        flex: 1,
        minWidth: 95,
        valueGetter: (value, row) => row.ledgerEventStatuses.eventItemStatuses.SITE,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'ledgerEventStatuses.eventItemStatuses.PATIENT',
        headerName: 'Patient',
        flex: 1,
        minWidth: 185,
        valueGetter: (value, row) => row.ledgerEventStatuses.eventItemStatuses.PATIENT,
        renderCell: ({ value, row }) => (
          <div>
            {value || '—'}
            {value === 'PAYMENT_FAILED' && !userHasRole(ROLE_FINANCE) && <PatientLedgerMenu row={row} />}
          </div>
        )
      },
      {
        field: 'ledgerEventStatuses.eventItemStatuses.VENDOR',
        headerName: 'Vendor',
        flex: 1,
        minWidth: 115,
        valueGetter: (value, row) => row.ledgerEventStatuses.eventItemStatuses.VENDOR,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'ledgerEventStatuses.eventItemStatuses.ELLIGO',
        headerName: 'Net',
        flex: 1,
        minWidth: 95,
        valueGetter: (value, row) => row.ledgerEventStatuses.eventItemStatuses.ELLIGO,
        renderCell: DefaultFinanceCell
      },
      {
        field: 'nonInvoiceable',
        headerName: 'Non-Invoiceable',
        minWidth: 180,
        valueGetter: (value, row) => (value ? 'Yes' : 'No')
      },
      {
        field: 'expenseEventComment',
        headerName: 'Expense Comments',
        flex: 1,
        minWidth: 190,
        renderCell: DefaultFinanceCell,
        valueFormatter: value => prepareNameForCSV(value)
      }
    ],
    []
  );
  return (
    <FinanceTableMUI
      localStorageTableName="LEDGER"
      rows={ledgerEvents}
      columns={columns}
      footerElementsCount={ledgerEvents.length}
      disableRowSelectionOnClick
      sx={{ padding: '0 40px 0 40px' }}
      localeText={muiDataGridLocaleText({ noRowsLabel: 'No Record Found' })}
      initialState={{
        columns: {
          columnVisibilityModel: { nonInvoiceable: false }
        },
        sorting: {
          sortModel: [{ field: 'eventNumber', sort: 'desc' }]
        }
      }}
      tableVersion="1.03"
    />
  );
};
